import WorkflowCards from "../components/WorkflowCards";
import EmptyState from "../components/EmptyState";
import { WORKFLOWS } from "../domain/workflows";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const HOME_HANDOFFS = [
  WORKFLOWS.find(item => item.id === "listing-agreement"),
  {
    ...WORKFLOWS.find(item => item.id === "pending-without-record"),
    title: "New Pending Transaction",
    description: "Send an executed Agreement of Sale to the transaction coordinator."
  }
].filter(Boolean);

function canSubmitTransactions(user) {
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return permissions.includes("*") || permissions.includes("submit_transactions");
}

function toDate(value) {
  if (!value) return null;
  const source = String(value);
  const date = new Date(DATE_ONLY.test(source) ? `${source}T12:00:00` : source);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameDay(left, right) {
  return left && right
    && left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function isComplete(task) {
  return ["complete", "completed", "done", "closed"].includes(String(task?.status || "").toLowerCase());
}

function isOverdue(task, now) {
  const due = toDate(task?.dueDate);
  return due && due < now && !isComplete(task);
}

function greetingFor(date) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function formatTime(value) {
  const date = toDate(value);
  if (!date || DATE_ONLY.test(String(value))) return "";
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(date);
}

function scheduleLabel(value, now) {
  const date = toDate(value);
  if (!date) return "Date pending";
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = isSameDay(date, now)
    ? "Today"
    : isSameDay(date, tomorrow)
      ? "Tomorrow"
      : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
  const time = formatTime(value);
  return time ? `${day} · ${time}` : day;
}

function money(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(Number(value || 0));
}

function compactNumber(value) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(Number(value || 0));
}

function belongsToUser(transaction, user) {
  const agentEmail = String(transaction?.agentEmail || "").toLowerCase();
  const agentName = String(transaction?.agentName || "").toLowerCase();
  const userEmail = String(user?.email || "").toLowerCase();
  const userName = String(user?.name || "").toLowerCase();
  return (!agentEmail && !agentName) || agentEmail === userEmail || agentName === userName;
}

function DashboardItem({ title, detail, meta, attention = false }) {
  return (
    <div className={`home-list-item${attention ? " home-list-item--attention" : ""}`}>
      <div>
        <strong>{title}</strong>
        {detail && <span>{detail}</span>}
      </div>
      {meta && <small>{meta}</small>}
    </div>
  );
}

export default function HomePage({ user, summary = {}, transactions = [], tasks = [], calendar = [], onStart }) {
  const now = new Date();
  const firstName = String(user?.name || "Team Member").split(" ")[0];
  const intakeAuthorized = canSubmitTransactions(user);
  const todayTasks = tasks.filter(task => isSameDay(toDate(task.dueDate), now));
  const completedToday = todayTasks.filter(isComplete).length;
  const rhythmTotal = Number.isFinite(Number(summary.rhythmTotal)) ? Number(summary.rhythmTotal) : todayTasks.length;
  const rhythmCompleted = Number.isFinite(Number(summary.rhythmCompleted)) ? Number(summary.rhythmCompleted) : completedToday;
  const rhythmPercent = rhythmTotal > 0 ? Math.min(100, Math.round((rhythmCompleted / rhythmTotal) * 100)) : 0;
  const finishEstimate = summary.rhythmEstimatedFinish || (rhythmTotal > 0 && rhythmCompleted >= rhythmTotal ? "Complete" : "Not available");

  const todayEvents = calendar
    .filter(event => isSameDay(toDate(event.date), now))
    .sort((a, b) => (toDate(a.date)?.getTime() || 0) - (toDate(b.date)?.getTime() || 0));
  const todayPlan = [
    ...todayEvents.map(event => ({ id: `event-${event.id}`, title: event.title, detail: event.propertyAddress || event.source || "Team Calendar", meta: formatTime(event.date) || "Today" })),
    ...todayTasks.filter(task => !isComplete(task)).map(task => ({ id: `task-${task.id}`, title: task.title, detail: task.propertyAddress || "Task", meta: "Due today" }))
  ].slice(0, 5);

  const awaitingReview = transactions
    .filter(transaction => transaction.intakeStatus === "Submitted for Review")
    .map(transaction => ({ id: `review-${transaction.id}`, title: transaction.propertyAddress || transaction.clientName || "Transaction intake", detail: "Transaction intake requires review", meta: "Awaiting Review" }));
  const overdueItems = tasks
    .filter(task => isOverdue(task, now))
    .map(task => ({ id: `overdue-${task.id}`, title: task.title, detail: task.propertyAddress || "Assigned task", meta: "Overdue" }));
  const needsAttention = [...awaitingReview, ...overdueItems].slice(0, 5);

  const currentYear = now.getFullYear();
  const productionTransactions = transactions.filter(transaction => belongsToUser(transaction, user));
  const closedYtd = productionTransactions.filter(transaction => {
    const closingDate = toDate(transaction.closingDate);
    return String(transaction.status || "").toLowerCase() === "closed" && closingDate?.getFullYear() === currentYear;
  });
  const ytdVolume = closedYtd.reduce((total, transaction) => total + Number(transaction.salePrice || 0), 0);
  const expectedGci = productionTransactions.reduce((total, transaction) => {
    const status = String(transaction.status || "").toLowerCase();
    if (["closed", "terminated", "cancelled", "canceled", "expired"].includes(status)) return total;
    return total + Number(transaction.adjustedGci || 0);
  }, 0);
  const upcoming = calendar
    .filter(event => {
      const date = toDate(event.date);
      return date && date >= new Date(now.getFullYear(), now.getMonth(), now.getDate());
    })
    .sort((a, b) => (toDate(a.date)?.getTime() || 0) - (toDate(b.date)?.getTime() || 0))
    .slice(0, 6);

  return (
    <div className="roots-home">
      <header className="home-command-header">
        <h1>{greetingFor(now)}, {firstName}</h1>
        <p>Your command center for today&apos;s priorities, schedule, and business execution.</p>
      </header>

      <div className="home-dashboard-grid">
        {intakeAuthorized && <section className="home-dashboard-card home-dashboard-card--wide home-quick-actions">
          <h2>Quick Actions</h2>
          <p className="home-card-support">Start important business workflows.</p>
          <WorkflowCards onStart={onStart} compact items={HOME_HANDOFFS} />
        </section>}

        <section className="home-dashboard-card home-rhythm-card">
          <h2>Today&apos;s Rhythm</h2>
          <p className="home-card-support">Personal operating rhythm completion</p>
          <div className="rhythm-gauge" style={{ "--rhythm-progress": `${rhythmPercent}%` }}>
            <div className="rhythm-gauge__inner"><strong>{rhythmPercent}%</strong><span>Complete</span></div>
          </div>
          <p className="rhythm-summary"><strong>{rhythmCompleted} of {rhythmTotal} complete</strong></p>
          <p className="rhythm-estimate">Estimated finish: <strong>{finishEstimate}</strong></p>
        </section>

        <section className="home-dashboard-card">
          <h2>Today&apos;s Plan</h2>
          <div className="home-list">{todayPlan.length ? todayPlan.map(item => <DashboardItem key={item.id} {...item} />) : <EmptyState>No scheduled work today.</EmptyState>}</div>
        </section>

        <section className="home-dashboard-card">
          <h2>Needs Attention</h2>
          <div className="home-list">{needsAttention.length ? needsAttention.map(item => <DashboardItem key={item.id} {...item} attention />) : <EmptyState>Nothing requires immediate attention.</EmptyState>}</div>
        </section>

        <section className="home-dashboard-card">
          <h2>My Production</h2>
          <div className="production-list">
            <DashboardItem title={`${closedYtd.length} YTD Transactions`} />
            <DashboardItem title={`${money(ytdVolume)} Volume`} detail={ytdVolume >= 1000000 ? `${compactNumber(ytdVolume)} closed this year` : "Closed volume this year"} />
            <DashboardItem title={`${money(expectedGci)} Expected GCI`} />
          </div>
        </section>

        <section className="home-dashboard-card home-dashboard-card--wide">
          <h2>Upcoming Schedule</h2>
          <div className="home-list home-list--schedule">
            {upcoming.length ? upcoming.map(event => <DashboardItem key={event.id} title={event.title} detail={event.propertyAddress || event.source || "Team Calendar"} meta={scheduleLabel(event.date, now)} />) : <EmptyState>No upcoming dates.</EmptyState>}
          </div>
        </section>
      </div>
    </div>
  );
}
